'use client';

import { ReactNode, useState } from 'react';

import classNames from 'classnames';

import { CTAButton } from '../../Clickables/Buttons/CTAButton/CTAButton';
import { Heading, IHeadingProps } from '../../Heading/Heading';
import { InfoIcon } from '../../Icons/InfoIcon';
import { Modal } from '../../Modal/Modal';
import { ModalBody } from '../../Modal/ModalBody';
import { Text } from '../../Text/Text';

export interface IInfoCardProps {
  className?: string;
  title: string;
  iconText: string;
  bottomText?: string;
  iconTextProps?: Partial<IHeadingProps>;
  iconComponent?: ReactNode;
  bgColorClassname?: string;
  headingColorClassName?: 'text-abyss' | 'text-success-shade' | 'text-error-shade' | 'text-trench';
  placeIconAboveIconText?: boolean;
  modalContent?: ReactNode;
  disabled?: boolean;
  behindSignup?: boolean;
  size?: 'lg' | 'sm';
  dataTestid?: string;
}

const iconClassName = 'max-w-[28px] max-h-[28px] md:max-w-[32px] md:max-h-[32px]';

export const InfoCard = ({
  className = '',
  title,
  iconText,
  bottomText,
  iconComponent,
  bgColorClassname = 'bg-lake-fog',
  headingColorClassName = 'text-abyss',
  placeIconAboveIconText = false,
  modalContent,
  disabled = false,
  behindSignup,
  iconTextProps = {},
  size = 'lg',
  dataTestid,
}: IInfoCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (): void => {
    if (modalContent) {
      setIsModalOpen(true);
    }
  };

  return (
    <div
      className={classNames(
        `min-h-[130px] sm:min-h-[138px] ${size === 'sm' ? 'md:min-h-[116px]' : 'md:min-h-[165px]'}`,
        bgColorClassname,
        className,
        'flex flex-col justify-between rounded-md p-2.5 sm:p-3 md:rounded-lg md:p-4',
        modalContent ? 'curson-pointer' : 'cursor-default',
      )}
      {...(modalContent && { onClick: handleOpenModal, onKeyPress: handleOpenModal, tabIndex: 0 })}
      role={modalContent ? 'button' : 'note'}
      data-testid="info-card"
    >
      <div className="flex flex-row items-center">
        <Text className="text-abyss mb-0" variant="sm">
          {title}
        </Text>
        {modalContent && (
          <div
            className="text-midnight hover:text-trench ml-auto size-5 cursor-pointer"
            data-testid={dataTestid}
          >
            <InfoIcon />
          </div>
        )}
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
            }}
          >
            <ModalBody>{modalContent}</ModalBody>
          </Modal>
        )}
      </div>
      {behindSignup && (
        <CTAButton type="signup" source="logged-catches-card" className="pt-3 pb-2 underline">
          Sign up for free
        </CTAButton>
      )}
      <div
        className={`flex ${
          placeIconAboveIconText
            ? 'mr-2.5 flex-col align-bottom md:mr-3'
            : 'items-end space-x-2.5 md:space-x-3'
        } ${behindSignup ? 'blur-xs' : ''} mt-auto`}
      >
        {iconComponent && (
          <div
            className={
              placeIconAboveIconText
                ? `${iconClassName} mb-2 sm:mb-3 ${disabled ? 'text-abyss' : ''}`
                : iconClassName
            }
          >
            {iconComponent}
          </div>
        )}
        <Heading
          as="div"
          variant="2xs"
          className={`mb-px break-words ${disabled ? 'text-abyss' : headingColorClassName}`}
          {...iconTextProps}
        >
          {iconText}
        </Heading>
      </div>
      {bottomText && (
        <Text
          className={`mt-3 mb-0 font-semibold break-words ${behindSignup && 'blur-xs'}`}
          variant="sm"
        >
          {bottomText}
        </Text>
      )}
    </div>
  );
};
